import { connect } from "react-redux";
import { ReduxState } from "../../store/state";
import NavBar from "../../components/navigation/NavBar";
import 'react-toastify/dist/ReactToastify.css';
import {
  Container,
  Text,
  Flex,
  Divider,
  Box,
} from '@chakra-ui/react'
import { User } from "../../store/user/types";
import { MdHistory, MdShoppingCart } from "react-icons/md";
import { useEffect } from "react";
import { fetchPreviouslyPurchasedItems, fetchPreviouslyViewedItems } from "../../store/page/initiators";
import { BusinessItem } from "../../store/page/types";
import MiniItemRow from "../../components/page/MiniItemRow";
import Footer from "../../components/navigation/Footer";
import { Cart, CartItem, SubmittedOrder } from "../../store/cart/types";
import { asyncCartCreate } from "../../store/cart/initiators";
import { fetchItemReferences } from "../../store/cart/initiators";
import CartCheckoutFlow from "../../components/cart/CartCheckoutFlow";
import { Helmet } from "react-helmet";

interface CartPageProps {
  user: User | null;
  previouslyViewedItems: BusinessItem[];
  previouslyPurchasedItems: BusinessItem[];
  isLoggedIn: boolean;
  cart: Cart | null;
  itemReferences: BusinessItem[];
  submittedOrders: SubmittedOrder[];
  fetchPreviouslyViewedItems: () => void;
  fetchPreviouslyPurchasedItems: () => void;
  fetchItemReferences: () => void;
}

const CartPage: React.FC<CartPageProps> = ({ 
  user, previouslyViewedItems, previouslyPurchasedItems, isLoggedIn, cart, itemReferences, submittedOrders,
  fetchPreviouslyViewedItems, fetchPreviouslyPurchasedItems, fetchItemReferences,
}) => {
  useEffect(() => {
    if (user) {
      fetchPreviouslyViewedItems();
      fetchPreviouslyPurchasedItems();
    }
  }, [user])

  useEffect(() => {
    asyncCartCreate()
  }, [cart])

  useEffect(() => {
    if (!itemReferences.length) {
      fetchItemReferences()
    }
  }, [itemReferences])

    // Group cart items by businessLocationId
    const groupedCartItems: Record<string, CartItem[]> = (cart?.items || []).reduce((groups: any, item) => {
      const { businessLocationId } = item;
      if (!groups[businessLocationId]) {
        groups[businessLocationId] = [];
      }
      groups[businessLocationId].push(item);
      return groups;
    }, {});


  const renderCartPage = () => {
    const hasLength = submittedOrders.length || cart?.items?.length
    if (isLoggedIn && user && hasLength) {

      return (
        <Box bg={'gray.50'}>
        <Container maxW={'8xl'} pt={1}>
          <Helmet>
            <title>{`Showfa Express Pages: Request. Deliver. Impress.`}</title>
            <meta name="description" content={`Showfa Express Pages: Request. Deliver. Impress.`} />
            <meta property="og:title" content={`Showfa Express Pages: Request. Deliver. Impress.`} />
            <meta property="og:description" content={`Showfa Express Pages: Request. Deliver. Impress.`} />
            <meta property="og:url" content={`/cart`} />
            <meta property="og:site_name" content="Showfa Express Pages" />
          </Helmet>
          <CartCheckoutFlow groupedCartItems={groupedCartItems} />

          { previouslyViewedItems.length ? (
            <>
            <Flex direction={'row'} alignItems={'center'} pt={2}>
              <MdHistory color="gray.600"/>
              <Text textAlign={'right'} fontWeight={'bold'} color={'gray.600'} ml={2}>Items You Viewed</Text>
            </Flex>
            <Divider></Divider>
            <MiniItemRow items={previouslyViewedItems.filter((item: BusinessItem) => (item.status === 'active' && item.pageData?.status === 'active'))} xs={true}/>
            </>
          ) : null}
          { previouslyPurchasedItems.length ? (
            <>
            <Flex direction={'row'} alignItems={'center'} pt={2}>
              <MdShoppingCart color="gray.600"/>
              <Text textAlign={'right'} fontWeight={'bold'} color={'gray.600'} ml={2}>Items You Bought</Text>
            </Flex>
            <Divider></Divider>
            <MiniItemRow items={previouslyPurchasedItems.filter((item: BusinessItem) => (item.status === 'active' && item.pageData?.status === 'active'))} xs={true}/>
            </>
          ): null}
        </Container>
        </Box>
      )
    }
    else {
      return( 
        <Box bg={'gray.50'}>
          <Text>There are no items in your cart.</Text>
        </Box>
      );
    }
  }

  return (
    <>
    <NavBar/>
    {renderCartPage()}
    <Footer />
    </>
  );
}

const mapStateToProps = (state: ReduxState) => {
  const { isLoggedIn } = state.auth;
  const { previouslyViewedItems, previouslyPurchasedItems } = state.page;
  const { user } = state.user;
  const { cart, itemReferences, submittedOrders } = state.cart;
  return { user, previouslyViewedItems, previouslyPurchasedItems, isLoggedIn, cart, itemReferences, submittedOrders };
};

export default connect(mapStateToProps, {
  fetchPreviouslyViewedItems,
  fetchPreviouslyPurchasedItems,
  fetchItemReferences,
})(CartPage);

